/**
 * Settings — Page de configuration (tabs)
 *
 * Auto-save: chaque modification déclenche PUT /api/settings/:key (debounced 600ms).
 * Indicateur visuel: ✓ vert à droite quand sauvegardé, ⟳ pendant save.
 * Bouton "Test" pour chaque API key → POST /api/settings/:key/test.
 * Toast vert "✓ Sauvegardé" en overlay top-right pendant 1s.
 * Chaque save crée une notification (cloche) + audit log (ship log).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MASKED_SECRET } from "@synapse/shared";
import { api } from "../lib/api";
import { showToast } from "../components/Toast";
import CommandersManager from "../components/CommandersManager";
import ObsidianConnection from "../components/ObsidianConnection";

interface DrpStepInfo {
  id: number;
  enabled: boolean;
  state: "pending" | "active" | "done" | "error";
}

const TABS = [
  { key: "llm", icon: "🧠", label: "LLM & IA", desc: "Z.ai · OpenAI · Routing" },
  { key: "github", icon: "🐱", label: "GitHub", desc: "Token · CI/CD · Repos" },
  { key: "discord", icon: "💬", label: "Discord", desc: "Bot · Channels" },
  { key: "obsidian", icon: "📓", label: "Obsidian", desc: "Vault · Notes" },
  { key: "notifications", icon: "🔔", label: "Notifications", desc: "Canaux · Heures silencieuses" },
  { key: "control", icon: "🎛", label: "Contrôle", desc: "Modes · Queue" },
  { key: "commanders", icon: "👑", label: "Commandants", desc: "Accès · Auth · Audit" },
  { key: "integrations", icon: "📡", label: "Santé intégrations", desc: "Temps réel · 10 briques" },
  { key: "network", icon: "🌐", label: "Réseau & Sécurité", desc: "Domaine · DB · WAF" },
  { key: "appearance", icon: "🎨", label: "Apparence", desc: "Thème · Langue" },
  { key: "drp", icon: "🆘", label: "Reprise & Backups", desc: "RTO · RPO · S3 · Wasabi" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const BASE = import.meta.env.VITE_API_URL || "/api";

// ── API helpers ─────────────────────────────────────────────────

interface SettingRow {
  key: string;
  value: string;
  isSecret: boolean;
  preview?: string;
  updatedAt?: string;
}

async function apiListSettings(): Promise<SettingRow[]> {
  try {
    const res = await fetch(`${BASE}/settings`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : data?.items ?? [];
  } catch {
    return [];
  }
}

async function apiPutSetting(
  key: string,
  value: string,
  isSecret = false,
): Promise<{ ok: boolean; updatedAt?: string }> {
  try {
    const res = await fetch(`${BASE}/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value, isSecret }),
    });
    if (!res.ok) return { ok: false };
    const data = await res.json();
    return { ok: true, updatedAt: data.updatedAt };
  } catch {
    return { ok: false };
  }
}

async function apiTestSetting(
  key: string,
  value?: string,
): Promise<{ ok: boolean; latencyMs?: number; message: string }> {
  try {
    const res = await fetch(`${BASE}/settings/${encodeURIComponent(key)}/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(value ? { value } : {}),
    });
    if (!res.ok) {
      return { ok: false, message: `Erreur HTTP ${res.status}` };
    }
    return await res.json();
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : "Erreur réseau",
    };
  }
}

// ── Hook: useSetting — gère l'état + auto-save d'un champ setting ──

type SaveStatus = "idle" | "saving" | "saved" | "error";

function useSetting(
  key: string,
  initialValue: string,
  label: string,
  isSecret = false,
  debounceMs = 600,
  initialStatus: SaveStatus = "idle",
) {
  const [value, setValue] = useState(initialValue);
  const [status, setStatus] = useState<SaveStatus>(initialStatus);
  const [lastSavedAt, setLastSavedAt] = useState<string | undefined>();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstRender = useRef(true);

  const save = useCallback(
    async (val: string) => {
      // ── CFG-04: ne jamais renvoyer la valeur masquée d'un secret ──
      // Si l'utilisateur n'a pas modifié un secret hydraté (champ = sentinelle
      // MASKED_SECRET), un PUT réécrirait la vraie clé par des points. On skip.
      // Note: l'appelant passe déjà `isSecret || type === "password"` en isSecret.
      if (isSecret && val === MASKED_SECRET) {
        setStatus("saved");
        setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
        return;
      }
      setStatus("saving");
      const res = await apiPutSetting(key, val, isSecret);
      if (res.ok) {
        setStatus("saved");
        setLastSavedAt(res.updatedAt);
        // Toast vert "Sauvegardé" en overlay top-right pendant 1s
        showToast("saved", "Sauvegardé", `${label || key} · ${new Date().toLocaleTimeString("fr-FR")}`, 1000);
        // Auto-dismiss "saved" after 1.5s — l'indicateur reste ✓ plus longtemps
        setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
      } else {
        setStatus("error");
        showToast("error", "Erreur", `Échec sauvegarde: ${label || key}`);
      }
    },
    [key, isSecret],
  );

  const onChange = useCallback(
    (newVal: string) => {
      setValue(newVal);
      setStatus("saving");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        save(newVal);
      }, debounceMs);
    },
    [save, debounceMs],
  );

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
      save(value);
    }
  }, [save, value]);

  // Sur unmount, flush si pending
  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        // Note: on ne peut pas await ici
      }
    };
  }, []);

  // Initial load flag
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      setStatus(initialValue ? "saved" : "idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { value, setValue, onChange, status, lastSavedAt, flush };
}

// ── Field components ────────────────────────────────────────────

function StatusIndicator({ status }: { status: SaveStatus }) {
  if (status === "saving") {
    return (
      <span
        title="Sauvegarde en cours…"
        style={{
          color: "var(--amber)",
          fontSize: "11px",
          fontFamily: "var(--mono)",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span>
        save…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span
        title="Sauvegardé"
        style={{
          color: "var(--olive)",
          fontSize: "13px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontWeight: 600,
        }}
      >
        ✓
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        title="Erreur de sauvegarde"
        style={{
          color: "var(--rust)",
          fontSize: "13px",
          fontWeight: 600,
        }}
      >
        ✗
      </span>
    );
  }
  return null;
}

/**
 * Champ avec auto-save + indicateur visuel ✓/⟳/✗
 */
function SettingsInput({
  settingKey,
  initialValue,
  label,
  type = "text",
  isSecret = false,
  showTestButton = false,
  hydratedValue,
  hydratedPersisted = false,
  previewValue,
}: {
  settingKey: string;
  initialValue: string;
  label: string;
  type?: "text" | "password" | "number";
  isSecret?: boolean;
  showTestButton?: boolean;
  hydratedValue?: string;
  hydratedPersisted?: boolean;
  previewValue?: string;
}) {
  // Si hydratedValue fourni, l'utiliser comme valeur initiale et marquer comme "saved"
  const initVal = hydratedValue ?? initialValue;
  const initStatus: SaveStatus = hydratedPersisted || hydratedValue ? "saved" : "idle";
  const { value, setValue, onChange, status } = useSetting(
    settingKey,
    initVal,
    label,
    isSecret || type === "password",
  );
  // Override initial status avec hydratedStatus au mount (useState initial)
  const initialStatusRef = useRef(initStatus);
  const effectiveStatus =
    status === "idle" && initialStatusRef.current === "saved" ? "saved" : status;
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
    latencyMs?: number;
  } | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    const res = await apiTestSetting(settingKey, value);
    setTestResult(res);
    setTesting(false);
    if (res.ok) {
      showToast("success", "Test réussi", `${label}: ${res.message}${res.latencyMs ? ` (${res.latencyMs}ms)` : ""}`);
    } else {
      showToast("error", "Test échoué", `${label}: ${res.message}`);
    }
  };

  return (
    <div className="field-group">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <label style={{ flex: 1 }}>{label}</label>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isSecret && previewValue && !value && (
            <span
              title="Aperçu du secret enregistré (3 premiers + 5 derniers caractères)"
              style={{ fontSize: "11px", fontFamily: "var(--mono)", color: "var(--muted)" }}
            >
              {previewValue}
            </span>
          )}
          <StatusIndicator status={effectiveStatus} />
        </div>
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <input
          className="field-input"
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isSecret && previewValue ? `Enregistré : ${previewValue} — saisir pour remplacer` : undefined}
          style={{ flex: 1 }}
        />
        {showTestButton && (
          <button
            className="btn"
            style={{ fontSize: "11px", padding: "8px 12px" }}
            onClick={handleTest}
            disabled={testing || !value}
            title={`Tester ${label}`}
          >
            {testing ? "⟳" : "Test"}
          </button>
        )}
      </div>
      {testResult && (
        <div
          style={{
            fontSize: "11px",
            marginTop: "4px",
            color: testResult.ok ? "var(--olive)" : "var(--rust)",
            fontFamily: "var(--mono)",
          }}
        >
          {testResult.ok ? "✓" : "✗"} {testResult.message}
          {testResult.latencyMs ? ` (${testResult.latencyMs}ms)` : ""}
        </div>
      )}
    </div>
  );
}

/**
 * Sélecteur de modèle — dropdown fetch depuis /api/llm/models?provider=…
 * + fallback texte. Si la liste échoue (pas de clé, erreur réseau), bascule
 * en saisie manuelle. `provider` détermine la source (zai par défaut, openai).
 */
function ModelSelect({
  settingKey,
  initialValue,
  label,
  hydratedValue,
  hydratedPersisted,
  provider = "zai",
}: {
  settingKey: string;
  initialValue: string;
  label: string;
  hydratedValue?: string;
  hydratedPersisted?: boolean;
  provider?: "zai" | "openai";
}) {
  const initVal = hydratedValue ?? initialValue;
  const initStatus: SaveStatus = hydratedPersisted || hydratedValue ? "saved" : "idle";
  const { value, onChange, status } = useSetting(settingKey, initVal, label, false);

  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${BASE}/llm/models?provider=${provider}`);
      const data = await res.json();
      if (data.ok && Array.isArray(data.models) && data.models.length > 0) {
        setModels(data.models);
        setUseFallback(false);
      } else {
        setUseFallback(true);
        setFetchError(data.message || "Aucun modèle disponible");
      }
    } catch (err) {
      setUseFallback(true);
      setFetchError(err instanceof Error ? err.message : "Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  const initialStatusRef = useRef(initStatus);
  const effectiveStatus =
    status === "idle" && initialStatusRef.current === "saved" ? "saved" : status;

  // Garantir que la valeur actuelle est toujours visible dans la liste
  const options = value && !models.includes(value) ? [value, ...models] : models;

  return (
    <div className="field-group">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <label style={{ flex: 1 }}>{label}</label>
        <StatusIndicator status={effectiveStatus} />
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        {useFallback ? (
          <input
            className="field-input"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
            placeholder={initialValue}
          />
        ) : (
          <select
            className="field-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
            disabled={loading}
          >
            {options.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        )}
        <button
          className="btn"
          style={{ fontSize: "11px", padding: "8px 12px" }}
          onClick={fetchModels}
          disabled={loading}
          title="Rafraîchir la liste des modèles"
        >
          {loading ? "⟳" : "↻"}
        </button>
      </div>
      {fetchError && (
        <div style={{ fontSize: "11px", marginTop: "4px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
          ⚠ {fetchError} — saisie manuelle disponible
        </div>
      )}
    </div>
  );
}

/**
 * Sélecteur d'organisation GitHub — dropdown alimenté par le PAT.
 * Fallback en saisie texte si l'API échoue (pas de token, erreur réseau).
 */
function OrgSelect({
  settingKey,
  initialValue,
  label,
  hydratedValue,
  hydratedPersisted,
}: {
  settingKey: string;
  initialValue: string;
  label: string;
  hydratedValue?: string;
  hydratedPersisted?: boolean;
}) {
  const initVal = hydratedValue ?? initialValue;
  const initStatus: SaveStatus = hydratedPersisted || hydratedValue ? "saved" : "idle";
  const { value, onChange, status } = useSetting(settingKey, initVal, label, false);

  const [orgs, setOrgs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${BASE}/github/orgs`);
      const data = await res.json();
      if (data.ok && Array.isArray(data.orgs) && data.orgs.length > 0) {
        setOrgs(data.orgs);
        setUseFallback(false);
      } else {
        setUseFallback(true);
        setFetchError(data.message || "Aucune organisation accessible");
      }
    } catch (err) {
      setUseFallback(true);
      setFetchError(err instanceof Error ? err.message : "Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const initialStatusRef = useRef(initStatus);
  const effectiveStatus =
    status === "idle" && initialStatusRef.current === "saved" ? "saved" : status;

  const options = value && !orgs.includes(value) ? [value, ...orgs] : orgs;

  return (
    <div className="field-group">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <label style={{ flex: 1 }}>{label}</label>
        <StatusIndicator status={effectiveStatus} />
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        {useFallback ? (
          <input
            className="field-input"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
            placeholder={initialValue}
          />
        ) : (
          <select
            className="field-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
            disabled={loading}
          >
            {options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        )}
        <button
          className="btn"
          style={{ fontSize: "11px", padding: "8px 12px" }}
          onClick={fetchOrgs}
          disabled={loading}
          title="Rafraîchir depuis le PAT"
        >
          {loading ? "⟳" : "↻"}
        </button>
      </div>
      {fetchError && (
        <div style={{ fontSize: "11px", marginTop: "4px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
          ⚠ {fetchError} — saisie manuelle disponible
        </div>
      )}
    </div>
  );
}

interface RunnerInfo {
  id: number;
  name: string;
  os: string;
  status: string;
  busy: boolean;
  labels: string[];
}

/**
 * Sélecteur de runners GitHub — cases à cocher multi-sélection basées sur
 * les labels des self-hosted runners de l'organisation. La valeur persistée
 * reste au format `[label-a, label-b]` (compatible workflow YAML `runs-on`).
 * Fallback en saisie texte si l'API échoue.
 */
function RunnersSelect({
  settingKey,
  initialValue,
  label,
  hydratedValue,
  hydratedPersisted,
}: {
  settingKey: string;
  initialValue: string;
  label: string;
  hydratedValue?: string;
  hydratedPersisted?: boolean;
}) {
  const initVal = hydratedValue ?? initialValue;
  const initStatus: SaveStatus = hydratedPersisted || hydratedValue ? "saved" : "idle";
  const { value, onChange, status } = useSetting(settingKey, initVal, label, false);

  const [labels, setLabels] = useState<string[]>([]);
  const [runners, setRunners] = useState<RunnerInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  // Parse la valeur persistée `[a, b, c]` → Set de labels sélectionnés
  const parseSelected = (raw: string): string[] =>
    raw.replace(/^\[|\]$/g, "").split(",").map((s) => s.trim()).filter(Boolean);

  const selected = new Set(parseSelected(value));

  const fetchRunners = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${BASE}/github/runners`);
      const data = await res.json();
      if (data.ok && Array.isArray(data.labels) && data.labels.length > 0) {
        setLabels(data.labels);
        setRunners(Array.isArray(data.runners) ? data.runners : []);
        setUseFallback(false);
      } else {
        setUseFallback(true);
        setFetchError(data.message || "Aucun runner disponible");
      }
    } catch (err) {
      setUseFallback(true);
      setFetchError(err instanceof Error ? err.message : "Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRunners(); }, [fetchRunners]);

  const initialStatusRef = useRef(initStatus);
  const effectiveStatus =
    status === "idle" && initialStatusRef.current === "saved" ? "saved" : status;

  const toggleLabel = (lbl: string) => {
    const next = new Set(selected);
    if (next.has(lbl)) next.delete(lbl);
    else next.add(lbl);
    onChange(`[${[...next].join(", ")}]`);
  };

  return (
    <div className="field-group">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <label style={{ flex: 1 }}>{label}</label>
        <StatusIndicator status={effectiveStatus} />
      </div>

      {useFallback ? (
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <input
            className="field-input"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
            placeholder={initialValue}
          />
          <button className="btn" style={{ fontSize: "11px", padding: "8px 12px" }}
            onClick={fetchRunners} disabled={loading} title="Rafraîchir">
            {loading ? "⟳" : "↻"}
          </button>
        </div>
      ) : (
        <>
          <div style={{
            display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center",
            padding: "8px", borderRadius: "10px", background: "rgba(0,0,0,0.14)",
            border: "1px solid var(--line)",
          }}>
            {labels.map((lbl) => {
              const active = selected.has(lbl);
              return (
                <span
                  key={lbl}
                  onClick={() => toggleLabel(lbl)}
                  style={{
                    cursor: "pointer", fontSize: "11px", padding: "5px 10px", borderRadius: "999px",
                    border: `1px solid ${active ? "var(--olive)" : "var(--line)"}`,
                    background: active ? "rgba(140,170,90,0.16)" : "rgba(255,255,255,0.03)",
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    fontFamily: "var(--mono)",
                  }}
                >
                  {active ? "☑" : "☐"} {lbl}
                </span>
              );
            })}
            <button className="btn" style={{ fontSize: "11px", padding: "6px 10px", marginLeft: "auto" }}
              onClick={fetchRunners} disabled={loading} title="Rafraîchir depuis GitHub">
              {loading ? "⟳" : "↻"}
            </button>
          </div>
          {runners.length > 0 && (
            <div style={{ fontSize: "10px", marginTop: "6px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
              {runners.map((r) => (
                <span key={r.id} style={{ marginRight: "10px" }}>
                  {r.status === "online" ? "🟢" : "⚪"} {r.name}
                  {r.busy ? " (occupé)" : ""}
                </span>
              ))}
            </div>
          )}
          <div style={{ fontSize: "10px", marginTop: "4px", color: "var(--muted)" }}>
            Valeur enregistrée : <code>{value || "[]"}</code>
          </div>
        </>
      )}
      {fetchError && (
        <div style={{ fontSize: "11px", marginTop: "4px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
          ⚠ {fetchError} — saisie manuelle disponible
        </div>
      )}
    </div>
  );
}

/**
 * Sélecteur de registry — dropdown avec options courantes + saisie libre.
 * Toujours éditable : l'utilisateur peut choisir une valeur connue ou taper
 * un registry custom via l'option "Autre…".
 */
function RegistrySelect({
  settingKey,
  initialValue,
  label,
  hydratedValue,
  hydratedPersisted,
}: {
  settingKey: string;
  initialValue: string;
  label: string;
  hydratedValue?: string;
  hydratedPersisted?: boolean;
}) {
  const initVal = hydratedValue ?? initialValue;
  const initStatus: SaveStatus = hydratedPersisted || hydratedValue ? "saved" : "idle";
  const { value, onChange, status } = useSetting(settingKey, initVal, label, false);

  const COMMON = [
    "harbor.technosecur.com",
    "ghcr.io",
    "docker.io",
    "registry.gitlab.com",
    "quay.io",
  ];

  const [custom, setCustom] = useState(!COMMON.includes(initVal) && initVal !== "");

  const initialStatusRef = useRef(initStatus);
  const effectiveStatus =
    status === "idle" && initialStatusRef.current === "saved" ? "saved" : status;

  const options = value && !COMMON.includes(value) && !custom ? [value, ...COMMON] : COMMON;

  return (
    <div className="field-group">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <label style={{ flex: 1 }}>{label}</label>
        <StatusIndicator status={effectiveStatus} />
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        {custom ? (
          <input
            className="field-input"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
            placeholder={initialValue}
            autoFocus
          />
        ) : (
          <select
            className="field-input"
            value={COMMON.includes(value) ? value : (options[0] ?? "")}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
          >
            {options.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        )}
        <button
          className="btn"
          style={{ fontSize: "11px", padding: "8px 12px", whiteSpace: "nowrap" }}
          onClick={() => setCustom((c) => !c)}
          title={custom ? "Choisir dans la liste" : "Saisir un registry custom"}
        >
          {custom ? "Liste" : "Autre…"}
        </button>
      </div>
    </div>
  );
}

/**
 * Champ token webhook CI/CD avec génération serveur + instructions.
 * - Affiche l'état (configuré ou non).
 * - Bouton "Générer" → POST /api/cicd/generate-webhook-token (32 bytes crypto).
 * - Modal post-génération : montre le token une seule fois + explique où le
 *   coller (secret GitHub Actions X_CICD_TOKEN) et comment l'utiliser.
 */
function WebhookTokenField({
  label,
  hydratedPersisted,
}: {
  label: string;
  hydratedPersisted?: boolean;
}) {
  const [configured, setConfigured] = useState(!!hydratedPersisted);
  const [generating, setGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BASE}/cicd/generate-webhook-token`, { method: "POST" });
      const data = await res.json();
      if (data.ok && data.token) {
        setGeneratedToken(data.token);
        setConfigured(true);
        showToast("saved", "Token généré", "Copiez-le maintenant — il ne sera plus affiché.", 2000);
      } else {
        showToast("error", "Échec", data.message || "Impossible de générer le token");
      }
    } catch (err) {
      showToast("error", "Erreur réseau", err instanceof Error ? err.message : "Erreur");
    } finally {
      setGenerating(false);
    }
  };

  const copyToken = async () => {
    if (!generatedToken) return;
    try {
      await navigator.clipboard.writeText(generatedToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard peut être bloqué */ }
  };

  return (
    <div className="field-group">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <label style={{ flex: 1 }}>{label}</label>
        <span style={{
          fontSize: "11px", fontFamily: "var(--mono)",
          color: configured ? "var(--olive)" : "var(--muted)",
        }}>
          {configured ? "✓ configuré" : "⚪ non configuré"}
        </span>
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <input
          className="field-input"
          type="password"
          value={configured ? MASKED_SECRET : ""}
          readOnly
          style={{ flex: 1, opacity: 0.7 }}
          placeholder="Aucun token — cliquez sur Générer"
        />
        <button
          className="btn primary"
          style={{ fontSize: "11px", padding: "8px 14px", whiteSpace: "nowrap" }}
          onClick={generate}
          disabled={generating}
          title="Génère un token aléatoire de 256 bits et l'enregistre"
        >
          {generating ? "⟳…" : configured ? "Régénérer" : "Générer"}
        </button>
      </div>
      <div style={{ fontSize: "10px", marginTop: "4px", color: "var(--muted)" }}>
        Secret partagé envoyé par le pipeline CI/CD dans l'en-tête <code>x-cicd-token</code>.
        La régénération invalide immédiatement l'ancien token.
      </div>

      {/* Modal post-génération */}
      {generatedToken && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
          zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => setGeneratedToken(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "var(--bg)", borderRadius: "16px", border: "1px solid rgba(140,170,90,0.35)",
            padding: "28px", maxWidth: "560px", width: "92%", boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
            display: "grid", gap: "16px",
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "30px", marginBottom: "6px" }}>🔑</div>
              <h3 style={{ margin: 0, fontSize: "16px", color: "var(--text)" }}>Token webhook CI/CD généré</h3>
            </div>

            <div style={{
              padding: "10px 12px", borderRadius: "10px", background: "rgba(200,140,60,0.12)",
              border: "1px solid rgba(200,140,60,0.3)", fontSize: "12px", color: "#ffcf9e",
            }}>
              ⚠ <strong>Copiez ce token maintenant.</strong> Pour des raisons de sécurité, il ne sera
              plus jamais affiché en clair — seule une version masquée apparaîtra ensuite.
            </div>

            <div>
              <p style={{ fontSize: "12px", margin: "0 0 6px", color: "var(--muted)" }}>
                <strong>1.</strong> Le token (déjà enregistré côté Synapse) :
              </p>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <code style={{
                  fontSize: "12px", wordBreak: "break-all", flex: 1, padding: "10px 12px",
                  background: "rgba(0,0,0,0.35)", borderRadius: "8px", color: "#c7f0a8",
                  fontFamily: "var(--mono)",
                }}>
                  {generatedToken}
                </code>
                <button className="btn" style={{ fontSize: "11px", padding: "10px 12px", whiteSpace: "nowrap" }}
                  onClick={copyToken}>
                  {copied ? "✓ Copié" : "Copier"}
                </button>
              </div>
            </div>

            <div>
              <p style={{ fontSize: "12px", margin: "0 0 6px", color: "var(--muted)" }}>
                <strong>2.</strong> Ajoutez-le comme secret dans GitHub :
              </p>
              <div style={{
                fontSize: "11px", lineHeight: 1.6, padding: "10px 12px", borderRadius: "8px",
                background: "rgba(0,0,0,0.2)", color: "var(--text)", fontFamily: "var(--mono)",
              }}>
                Repo/Org → <strong>Settings</strong> → <strong>Secrets and variables</strong> →{" "}
                <strong>Actions</strong> → <strong>New secret</strong><br />
                Nom : <code style={{ color: "#c7f0a8" }}>SYNAPSE_CICD_TOKEN</code><br />
                Valeur : <em>collez le token ci-dessus</em>
              </div>
            </div>

            <div>
              <p style={{ fontSize: "12px", margin: "0 0 6px", color: "var(--muted)" }}>
                <strong>3.</strong> Utilisez-le dans le workflow (en-tête HTTP) :
              </p>
              <code style={{
                display: "block", fontSize: "11px", padding: "10px 12px", borderRadius: "8px",
                background: "rgba(0,0,0,0.2)", color: "#ddd0ff", fontFamily: "var(--mono)", whiteSpace: "pre-wrap",
              }}>
{`curl -X POST http://synapse-api:8000/api/cicd/events \\
  -H "x-cicd-token: \${{ secrets.SYNAPSE_CICD_TOKEN }}" \\
  -H "Content-Type: application/json" \\
  -d '{ "project": "synapse", "status": "success" }'`}
              </code>
            </div>

            <button className="btn primary" style={{ fontSize: "12px" }}
              onClick={() => setGeneratedToken(null)}>
              J'ai copié le token — Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Wrapper pour rétro-compatibilité avec l'ancienne API Field
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="field-group">
      <label>{label}</label>
      {children}
    </div>
  );
}

// ── Settings panels ──────────────────────────────────────────────

function SettingsCard({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="settings-card">
      <h3>{icon} {title}</h3>
      {children}
    </div>
  );
}

interface SettingsPanelProps {
  getValue: (key: string, fallback: string) => string;
  hasPersisted: (key: string) => boolean;
  getPreview?: (key: string) => string | undefined;
}

// ── OpenAI Auth: sélecteur API Key / OAuth (Login with ChatGPT) ──

function OpenAiAuthSection({ getValue, hasPersisted }: SettingsPanelProps) {
  const [mode, setMode] = useState<"api_key" | "oauth">(
    (getValue("llm.openai.auth_mode", "api_key") as "api_key" | "oauth") || "api_key",
  );
  const [status, setStatus] = useState<Awaited<ReturnType<typeof api.oauthOpenai.status>> | null>(null);
  const [connecting, setConnecting] = useState(false);

  const refreshStatus = useCallback(async () => {
    try { setStatus(await api.oauthOpenai.status()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // Persiste le mode choisi.
  const changeMode = (m: "api_key" | "oauth") => {
    setMode(m);
    apiPutSetting("llm.openai.auth_mode", m, false).catch(() => {});
  };

  const [deviceCode, setDeviceCode] = useState<{ verificationUrl?: string; userCode?: string } | null>(null);
  const [waiting, setWaiting] = useState(false);

  const connect = async () => {
    setConnecting(true);
    setDeviceCode(null);
    try {
      // Étape 1: lancer le process — retourne immédiatement
      await api.oauthOpenai.deviceCode.start();
      setConnecting(false);
      setWaiting(true);

      // Étape 2: Poll pour obtenir le device code (max 30s)
      const codeStart = Date.now();
      const codeTimer = setInterval(async () => {
        const poll = await api.oauthOpenai.deviceCode.poll().catch(() => null);
        if (poll?.status === "ready" && poll.verificationUrl && poll.userCode) {
          clearInterval(codeTimer);
          // Afficher le modal avec le code
          setDeviceCode({ verificationUrl: poll.verificationUrl, userCode: poll.userCode });
          if (poll.verificationUrl) window.open(poll.verificationUrl, "_blank");

          // Étape 3: Poll pour détecter la complétion (max 15 min)
          const authStart = Date.now();
          const authTimer = setInterval(async () => {
            if (Date.now() - authStart > 15 * 60 * 1000) {
              clearInterval(authTimer);
              setWaiting(false);
              showToast("error", "Expiration", "Le code a expiré. Relancez la connexion.");
              return;
            }
            const p = await api.oauthOpenai.deviceCode.poll().catch(() => null);
            // Quand le process se termine, l'API peut importer auth.json
            // On tente complete() après un délai raisonnable
            if (p?.status === "completed" || p?.status === "already_connected") {
              clearInterval(authTimer);
              setWaiting(false);
              await completeLogin();
            }
          }, 3000);
        } else if (poll?.status === "already_connected") {
          clearInterval(codeTimer);
          setWaiting(false);
          await completeLogin();
        } else if (poll?.status === "error") {
          clearInterval(codeTimer);
          setWaiting(false);
          setConnecting(false);
          showToast("error", "Erreur Codex", poll.error || "Impossible d'obtenir le code.");
        } else if (Date.now() - codeStart > 30_000) {
          clearInterval(codeTimer);
          setWaiting(false);
          setConnecting(false);
          showToast("error", "Timeout", "Codex CLI n'a pas répondu dans les 30s.");
        }
      }, 2000);
    } catch (err) {
      showToast("error", "Connexion impossible", err instanceof Error ? err.message : "Erreur");
      setConnecting(false);
      setWaiting(false);
    }
  };

  const completeLogin = async () => {
    setConnecting(true);
    try {
      const result = await api.oauthOpenai.deviceCode.complete();
      showToast("success", "ChatGPT connecté", result.message);
      setDeviceCode(null);
      refreshStatus();
    } catch (err) {
      showToast("error", "Import échoué", err instanceof Error ? err.message : "Erreur");
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    await api.oauthOpenai.logout().catch(() => {});
    showToast("info", "OpenAI déconnecté", "Tokens OAuth effacés.");
    refreshStatus();
  };

  return (
    <div style={{ display: "grid", gap: "10px" }}>
      {/* Sélecteur de mode */}
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <span style={{ fontSize: "12px", color: "var(--muted)" }}>Mode d'authentification :</span>
        {(["api_key", "oauth"] as const).map((m) => (
          <span
            key={m}
            onClick={() => changeMode(m)}
            className={`chip ${mode === m ? "active" : ""}`}
            style={{
              cursor: "pointer", fontSize: "11px", padding: "5px 12px", borderRadius: "999px",
              border: "1px solid var(--line)",
              background: mode === m ? "rgba(183,148,246,0.16)" : "rgba(255,255,255,0.04)",
            }}
          >
            {m === "api_key" ? "🔑 API Key" : "🔓 Login ChatGPT (OAuth)"}
          </span>
        ))}
      </div>

      {mode === "api_key" && (
        <SettingsInput
          settingKey="llm.openai.api_key"
          initialValue=""
          label="API Key"
          type="password"
          isSecret
          showTestButton
          hydratedValue={getValue("llm.openai.api_key", "")}
          hydratedPersisted={hasPersisted("llm.openai.api_key")}
        />
      )}

      {mode === "oauth" && (
        <div style={{ display: "grid", gap: "10px" }}>
          {/* État de connexion */}
          <div style={{
            padding: "12px", borderRadius: "12px", background: "rgba(0,0,0,0.14)",
            border: `1px solid ${status?.connected ? "var(--olive)" : "var(--line)"}40`,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
          }}>
            <div style={{ fontSize: "13px" }}>
              {status?.connected ? (
                <span style={{ color: "var(--olive)" }}>
                  ✅ Connecté{status.account ? ` — ${status.account}` : ""}
                  {status.expired ? " (token expiré, refresh au prochain appel)" : ""}
                </span>
              ) : (
                <span style={{ color: "var(--muted)" }}>⚪ Non connecté</span>
              )}
            </div>
            {status?.connected ? (
              <button className="btn" style={{ fontSize: "11px" }} onClick={disconnect}>Déconnexion</button>
            ) : (
              <button className="btn primary" style={{ fontSize: "11px" }} onClick={connect} disabled={connecting}>
                {connecting ? "⟳…" : "Connecter"}
              </button>
            )}
          </div>

          {/* Modal Device Code — overlay central */}
          {deviceCode && (
            <>
              {/* Backdrop */}
              <div style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
                zIndex: 9998,
                display: "flex", alignItems: "center", justifyContent: "center",
              }} onClick={() => { if (!waiting) setDeviceCode(null); }}>
                {/* Modal */}
                <div onClick={e => e.stopPropagation()} style={{
                  background: "var(--bg)", borderRadius: "16px",
                  border: "1px solid rgba(183,148,246,0.3)",
                  padding: "32px", maxWidth: "440px", width: "90%",
                  boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
                  zIndex: 9999,
                  display: "grid", gap: "16px",
                }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>🔐</div>
                    <h3 style={{ margin: 0, fontSize: "16px", color: "var(--text)" }}>
                      Connexion ChatGPT
                    </h3>
                  </div>

                  {/* Étape 1 — URL */}
                  <div>
                    <p style={{ fontSize: "12px", margin: "0 0 6px", color: "var(--muted)" }}>
                      <strong>1.</strong> Ouvrez cette URL dans votre navigateur :
                    </p>
                    <div style={{
                      display: "flex", gap: "6px", alignItems: "center",
                    }}>
                      <code style={{
                        fontSize: "11px", wordBreak: "break-all", flex: 1,
                        padding: "8px 10px", background: "rgba(0,0,0,0.3)",
                        borderRadius: "8px", color: "#ddd0ff",
                      }}>
                        {deviceCode.verificationUrl}
                      </code>
                      <button className="btn" style={{ fontSize: "10px", padding: "8px 10px", whiteSpace: "nowrap" }}
                        onClick={() => { if (deviceCode.verificationUrl) window.open(deviceCode.verificationUrl, "_blank"); }}>
                        Ouvrir ↗
                      </button>
                    </div>
                  </div>

                  {/* Étape 2 — Code */}
                  <div>
                    <p style={{ fontSize: "12px", margin: "0 0 6px", color: "var(--muted)" }}>
                      <strong>2.</strong> Entrez ce code :
                    </p>
                    <code style={{
                      fontSize: "28px", fontWeight: 700, letterSpacing: "0.15em",
                      display: "block", textAlign: "center", padding: "14px",
                      background: "rgba(183,148,246,0.12)",
                      border: "1px solid rgba(183,148,246,0.24)",
                      borderRadius: "10px", color: "#ddd0ff",
                      fontFamily: "var(--mono)",
                    }}>
                      {deviceCode.userCode}
                    </code>
                  </div>

                  {/* Étape 3 — Statut : tout automatique, pas de bouton */}
                  <div style={{ textAlign: "center" }}>
                    <p style={{ fontSize: "12px", margin: 0, color: "var(--olive)" }}>
                      ⏳ En attente de votre autorisation…<br />
                      <span style={{ fontSize: "11px", color: "var(--muted)" }}>
                        La connexion se complètera automatiquement dès que vous aurez autorisé sur ChatGPT.
                      </span>
                    </p>
                  </div>

                  <button className="btn ghost" style={{ fontSize: "11px", color: "var(--muted)" }}
                    onClick={() => { setDeviceCode(null); setWaiting(false); }}>
                    Annuler
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Note: l'auth se fait via Device Code (Codex CLI).
              Aucun Issuer/Client ID/Scope à configurer — tout est géré par le flux device code. */}
        </div>
      )}
    </div>
  );
}

function LlmSettings({ getValue, hasPersisted, getPreview }: SettingsPanelProps) {
  return (
    <>
      <SettingsCard icon="🧠" title="Z.ai">
        <SettingsInput
          settingKey="llm.zai.api_key"
          initialValue=""
          label="API Key"
          type="password"
          isSecret
          showTestButton
          hydratedValue={getValue("llm.zai.api_key", "")}
          hydratedPersisted={hasPersisted("llm.zai.api_key")}
          previewValue={getPreview?.("llm.zai.api_key")}
        />
        <SettingsInput
          settingKey="llm.zai.endpoint"
          initialValue="https://api.z.ai/api/coding/paas/v4"
          label="Endpoint"
          hydratedValue={getValue("llm.zai.endpoint", "https://api.z.ai/api/coding/paas/v4")}
          hydratedPersisted={hasPersisted("llm.zai.endpoint")}
        />
        <ModelSelect
          settingKey="llm.zai.model"
          initialValue="glm-4.7"
          label="Modèle par défaut"
          hydratedValue={getValue("llm.zai.model", "glm-4.7")}
          hydratedPersisted={hasPersisted("llm.zai.model")}
        />
        <SettingsInput
          settingKey="llm.zai.max_tokens"
          initialValue="8192"
          label="Max tokens"
          type="number"
          hydratedValue={getValue("llm.zai.max_tokens", "8192")}
          hydratedPersisted={hasPersisted("llm.zai.max_tokens")}
        />
      </SettingsCard>
      <SettingsCard icon="🤖" title="OpenAI">
        <OpenAiAuthSection getValue={getValue} hasPersisted={hasPersisted} />
        <ModelSelect
          settingKey="llm.openai.model"
          initialValue="o3"
          label="Modèle par défaut"
          provider="openai"
          hydratedValue={getValue("llm.openai.model", "o3")}
          hydratedPersisted={hasPersisted("llm.openai.model")}
        />
        <SettingsInput
          settingKey="llm.openai.max_tokens"
          initialValue="16384"
          label="Max tokens"
          type="number"
          hydratedValue={getValue("llm.openai.max_tokens", "16384")}
          hydratedPersisted={hasPersisted("llm.openai.max_tokens")}
        />
      </SettingsCard>
      <SettingsCard icon="⚙" title="Règles de routing">
        <p style={{ fontSize: "12px", color: "var(--muted)", margin: "0 0 8px" }}>
          Pour changer les règles de routage, modifier le champ ci-dessus (LLM &gt; Z.ai · Routing).
        </p>
      </SettingsCard>
    </>
  );
}

function GithubSettings({ getValue, hasPersisted, getPreview }: SettingsPanelProps) {
  return (
    <>
      <SettingsCard icon="🔐" title="Authentification">
        <SettingsInput
          settingKey="github.token"
          initialValue=""
          label="Token (PAT)"
          type="password"
          isSecret
          showTestButton
          hydratedValue={getValue("github.token", "")}
          hydratedPersisted={hasPersisted("github.token")}
          previewValue={getPreview?.("github.token")}
        />
        <SettingsInput
          settingKey="github.review_token"
          initialValue=""
          label="Token review — BoB-CI-review (PAT)"
          type="password"
          isSecret
          showTestButton
          hydratedValue={getValue("github.review_token", "")}
          hydratedPersisted={hasPersisted("github.review_token")}
          previewValue={getPreview?.("github.review_token")}
        />
        <OrgSelect
          settingKey="github.organization"
          initialValue="TechnoSecur"
          label="Organisation"
          hydratedValue={getValue("github.organization", "TechnoSecur")}
          hydratedPersisted={hasPersisted("github.organization")}
        />
      </SettingsCard>
      <SettingsCard icon="🏗" title="CI/CD">
        <RunnersSelect
          settingKey="github.runners"
          initialValue="[self-hosted, technosecur-self-hosted]"
          label="Runners"
          hydratedValue={getValue("github.runners", "[self-hosted, technosecur-self-hosted]")}
          hydratedPersisted={hasPersisted("github.runners")}
        />
        <RegistrySelect
          settingKey="github.registry"
          initialValue="harbor.technosecur.com"
          label="Registry"
          hydratedValue={getValue("github.registry", "harbor.technosecur.com")}
          hydratedPersisted={hasPersisted("github.registry")}
        />
        <WebhookTokenField
          label="Token webhook CI/CD (x-cicd-token)"
          hydratedPersisted={hasPersisted("cicd.webhook_token")}
        />
      </SettingsCard>
    </>
  );
}

function DiscordSettings({ getValue, hasPersisted, getPreview }: SettingsPanelProps) {
  return (
    <>
      <SettingsCard icon="🤖" title="Bot">
        <SettingsInput
          settingKey="discord.bot_token"
          initialValue=""
          label="Bot Token"
          type="password"
          isSecret
          showTestButton
          hydratedValue={getValue("discord.bot_token", "")}
          hydratedPersisted={hasPersisted("discord.bot_token")}
          previewValue={getPreview?.("discord.bot_token")}
        />
        <SettingsInput
          settingKey="discord.app_id"
          initialValue=""
          label="Application ID"
          hydratedValue={getValue("discord.app_id", "")}
          hydratedPersisted={hasPersisted("discord.app_id")}
        />
      </SettingsCard>
      <SettingsCard icon="📢" title="Channels">
        <SettingsInput
          settingKey="discord.missions_channel"
          initialValue="#synapse-missions"
          label="Missions"
          hydratedValue={getValue("discord.missions_channel", "#synapse-missions")}
          hydratedPersisted={hasPersisted("discord.missions_channel")}
        />
        <SettingsInput
          settingKey="discord.alerts_channel"
          initialValue="#synapse-alerts"
          label="Alertes"
          hydratedValue={getValue("discord.alerts_channel", "#synapse-alerts")}
          hydratedPersisted={hasPersisted("discord.alerts_channel")}
        />
      </SettingsCard>
    </>
  );
}

function ObsidianSettings({ getValue, hasPersisted, getPreview }: SettingsPanelProps) {
  const enabled = getValue("obsidian.enabled", "false").toLowerCase() === "true";
  const mode = (getValue("obsidian.mode", "external") === "local" ? "local" : "external") as "local" | "external";
  return (
    <>
      <ObsidianConnection
        initialEnabled={enabled}
        initialMode={mode}
        initialUrl={getValue("obsidian.api_url", "")}
        urlPersisted={hasPersisted("obsidian.api_url")}
        keyPreview={getPreview?.("obsidian.api_key")}
        keyPersisted={hasPersisted("obsidian.api_key")}
      />

      <SettingsCard icon="📂" title="Vault & dossiers cibles">
        <SettingsInput
          settingKey="obsidian.vault_path"
          initialValue="/home/hermes/Documents/Obsidian Vault"
          label="Vault path (nom logique du vault)"
          hydratedValue={getValue("obsidian.vault_path", "/home/hermes/Documents/Obsidian Vault")}
          hydratedPersisted={hasPersisted("obsidian.vault_path")}
        />
        <SettingsInput
          settingKey="obsidian.missions_dir"
          initialValue="Synapse/Missions/"
          label="Dossier missions (notes poussées)"
          hydratedValue={getValue("obsidian.missions_dir", "Synapse/Missions/")}
          hydratedPersisted={hasPersisted("obsidian.missions_dir")}
        />
        <SettingsInput
          settingKey="obsidian.journal_dir"
          initialValue="Synapse/Journal/"
          label="Dossier journal (comptes-rendus)"
          hydratedValue={getValue("obsidian.journal_dir", "Synapse/Journal/")}
          hydratedPersisted={hasPersisted("obsidian.journal_dir")}
        />
      </SettingsCard>

      <SettingsCard icon="🧭" title="Architecture de connexion">
        <div style={{ fontSize: "12px", color: "var(--muted)", lineHeight: 1.6 }}>
          <p style={{ marginTop: 0 }}>
            Le pont utilise le plugin <strong>« Local REST API »</strong> d'Obsidian : Synapse <em>pousse</em>
            missions & journal en HTTPS (clé Bearer). Deux topologies possibles selon le mode choisi ci-dessus.
          </p>
          <div style={{ display: "grid", gap: "10px", margin: "8px 0" }}>
            <div>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--olive)", marginBottom: "4px" }}>📦 Mode LOCAL — sidecar dans le pod</div>
              <pre style={{
                background: "rgba(0,0,0,0.24)", border: "1px solid var(--line)", borderRadius: "12px",
                padding: "12px", fontFamily: "var(--mono)", fontSize: "10.5px", lineHeight: 1.6,
                overflow: "auto", color: "#c9d4ff", margin: 0,
              }}>{`  ┌─────────── pod synapse-api (ns synapse) ───────────┐
  │  API  ──127.0.0.1:27123──▶  sidecar obsidian-rest  │
  │                             vault sur PVC          │
  └────────────────────────────────────────────────────┘
  URL + clé configurées AUTOMATIQUEMENT (bouton Provisionner)`}</pre>
            </div>
            <div>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--blue)", marginBottom: "4px" }}>🌐 Mode EXTERNE — poste distant</div>
              <pre style={{
                background: "rgba(0,0,0,0.24)", border: "1px solid var(--line)", borderRadius: "12px",
                padding: "12px", fontFamily: "var(--mono)", fontSize: "10.5px", lineHeight: 1.6,
                overflow: "auto", color: "#c9d4ff", margin: 0,
              }}>{`  ┌────────────┐  HTTPS+Bearer   ┌──────────────────────┐
  │ Synapse API│ ──────────────▶ │ Obsidian (poste)     │
  │ (pods K3s) │  PUT /vault/... │ plugin REST :27124   │
  └────────────┘                 └──────────────────────┘
  URL + clé saisies MANUELLEMENT (egress K3s requis)`}</pre>
            </div>
          </div>
          <p style={{ marginBottom: 0 }}>
            <strong>Local</strong> : un clic « Provisionner » génère la clé + fixe l'URL interne et fournit le
            manifeste sidecar à appliquer. <strong>Externe</strong> : installer le plugin sur le poste, activer HTTPS,
            renseigner l'URL joignable depuis K3s + la clé. Dans les deux cas, l'état apparaît dans « Santé intégrations ».
          </p>
        </div>
      </SettingsCard>
    </>
  );
}

function ControlSettings({ getValue, hasPersisted }: SettingsPanelProps) {
  return (
    <>
      <SettingsCard icon="🎛" title="Mode de contrôle">
        <Field label="Mode">
          <div className="select-row">
            <span className="select-pill">manuel</span>
            <span className="select-pill active">semi-auto</span>
            <span className="select-pill">auto</span>
          </div>
        </Field>
      </SettingsCard>
      <SettingsCard icon="🎯" title="Tentatives & satisfaction">
        <SettingsInput
          settingKey="control.max_retries"
          initialValue="6"
          label="Max tentatives"
          type="number"
          hydratedValue={getValue("control.max_retries", "6")}
          hydratedPersisted={hasPersisted("control.max_retries")}
        />
        <SettingsInput
          settingKey="control.success_target"
          initialValue="3"
          label="Objectif réussite"
          type="number"
          hydratedValue={getValue("control.success_target", "3")}
          hydratedPersisted={hasPersisted("control.success_target")}
        />
        <SettingsInput
          settingKey="control.min_satisfaction"
          initialValue="90"
          label="Satisfaction min (%)"
          type="number"
          hydratedValue={getValue("control.min_satisfaction", "90")}
          hydratedPersisted={hasPersisted("control.min_satisfaction")}
        />
      </SettingsCard>
      <SettingsCard icon="🕐" title="Polling">
        <SettingsInput
          settingKey="control.poll_interval"
          initialValue="5"
          label="Intervalle (s)"
          type="number"
          hydratedValue={getValue("control.poll_interval", "5")}
          hydratedPersisted={hasPersisted("control.poll_interval")}
        />
        <SettingsInput
          settingKey="control.max_parallel"
          initialValue="2"
          label="Missions parallèles max"
          type="number"
          hydratedValue={getValue("control.max_parallel", "2")}
          hydratedPersisted={hasPersisted("control.max_parallel")}
        />
      </SettingsCard>
    </>
  );
}

function CommandersSettings({ getValue, hasPersisted }: SettingsPanelProps) {
  const cfDomain = getValue("cf.domain", "synapse.ia.technosecur.com");
  return (
    <>
      <CommandersManager cfDomain={cfDomain} />
      <SettingsCard icon="🔐" title="Authentification CF Zero Trust">
        <SettingsInput
          settingKey="cf.domain"
          initialValue="synapse.ia.technosecur.com"
          label="Domaine protégé"
          hydratedValue={getValue("cf.domain", "synapse.ia.technosecur.com")}
          hydratedPersisted={hasPersisted("cf.domain")}
        />
        <Field label="Break-glass">
          <div className="select-row">
            <span className="select-pill active">Activé — surveillé</span>
            <span className="select-pill">Désactivé</span>
          </div>
        </Field>
        <div style={{ fontSize: "11px", color: "var(--muted)", lineHeight: 1.5, marginTop: "4px" }}>
          Les sessions et le MFA sont appliqués au bord par Cloudflare Access.
          Cette console gère l'annuaire et les rôles (RBAC) des commandants ;
          chaque création, modification ou suppression est journalisée dans l'audit (Ship Logs).
        </div>
      </SettingsCard>
    </>
  );
}

// ── Carte Base de données (statut live DB + Redis, pool CNPG, migrations) ──

function DatabaseStatusCard({ getValue, hasPersisted }: SettingsPanelProps) {
  const [db, setDb] = useState<{ status: string; detail: string; latencyMs: number | null } | null>(null);
  const [redis, setRedis] = useState<{ status: string; detail: string; latencyMs: number | null } | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await api.health.integrations();
      const d = data.find((x) => x.key === "db");
      const r = data.find((x) => x.key === "redis");
      setDb(d ? { status: d.status, detail: d.detail ?? "", latencyMs: d.latencyMs ?? null } : null);
      setRedis(r ? { status: r.status, detail: r.detail ?? "", latencyMs: r.latencyMs ?? null } : null);
    } catch {
      setDb(null); setRedis(null);
    } finally {
      setLoaded(true);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    load();
    const interval = setInterval(() => { if (alive) load(); }, 30_000);
    return () => { alive = false; clearInterval(interval); };
  }, [load]);

  const color = (s?: string) => s === "ok" ? "var(--olive)" : s === "degraded" ? "var(--amber)" : "var(--rust)";
  const label = (s?: string) => s === "ok" ? "connecté" : s === "degraded" ? "dégradé" : s === "down" ? "injoignable" : "inconnu";

  const StatusPill = ({ st }: { st?: string }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", fontWeight: 600, color: color(st) }}>
      <span style={{ width: "8px", height: "8px", borderRadius: "999px", background: color(st), boxShadow: st === "ok" ? `0 0 6px ${color(st)}` : undefined }} />
      {label(st)}
    </span>
  );

  return (
    <div className="settings-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <h3 style={{ margin: 0 }}>🗄 Base de données</h3>
        <button className="btn" style={{ fontSize: "11px", padding: "6px 10px" }} onClick={load} disabled={refreshing} title="Rafraîchir">
          {refreshing ? <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> : "↻"}
        </button>
      </div>

      {/* Statut live PostgreSQL + Redis */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "14px" }}>
        <div style={{ padding: "12px", borderRadius: "14px", background: "rgba(0,0,0,0.16)", border: `1px solid ${color(db?.status)}28`, display: "grid", gap: "6px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong style={{ fontSize: "12px" }}>🐘 PostgreSQL (CNPG)</strong>
            <StatusPill st={db?.status} />
          </div>
          <div style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
            {loaded ? `${db?.detail || "—"}${db?.latencyMs != null ? ` · ${db.latencyMs}ms` : ""}` : "…"}
          </div>
        </div>
        <div style={{ padding: "12px", borderRadius: "14px", background: "rgba(0,0,0,0.16)", border: `1px solid ${color(redis?.status)}28`, display: "grid", gap: "6px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong style={{ fontSize: "12px" }}>⚡ Redis (queue)</strong>
            <StatusPill st={redis?.status} />
          </div>
          <div style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
            {loaded ? `${redis?.detail || "—"}${redis?.latencyMs != null ? ` · ${redis.latencyMs}ms` : ""}` : "…"}
          </div>
        </div>
      </div>

      {/* Topologie CNPG (informatif, aligné plan Synapse) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginBottom: "14px" }}>
        <div style={{ padding: "10px", borderRadius: "12px", background: "rgba(0,0,0,0.12)", textAlign: "center" }}>
          <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>Cluster</div>
          <div style={{ fontSize: "13px", fontWeight: 600, fontFamily: "var(--mono)" }}>synapse-db</div>
        </div>
        <div style={{ padding: "10px", borderRadius: "12px", background: "rgba(0,0,0,0.12)", textAlign: "center" }}>
          <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>Endpoints</div>
          <div style={{ fontSize: "13px", fontWeight: 600, fontFamily: "var(--mono)" }}>-rw / -ro / -r</div>
        </div>
        <div style={{ padding: "10px", borderRadius: "12px", background: "rgba(0,0,0,0.12)", textAlign: "center" }}>
          <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>WAL / backup</div>
          <div style={{ fontSize: "13px", fontWeight: 600, fontFamily: "var(--mono)", color: "var(--olive)" }}>continu</div>
        </div>
      </div>

      {/* Chaînes de connexion — documentation/override (mot de passe réel dans les secrets K8s).
          Affichées en clair (password masqué par ***) pour montrer la topologie CNPG. */}
      <SettingsInput
        settingKey="network.db_connection"
        initialValue="postgresql://synapse:***@synapse-db-rw.synapse.svc:5432/synapse"
        label="Connection (primaire RW)"
        hydratedValue={getValue("network.db_connection", "postgresql://synapse:***@synapse-db-rw.synapse.svc:5432/synapse")}
        hydratedPersisted={hasPersisted("network.db_connection")}
      />
      <SettingsInput
        settingKey="network.db_connection_ro"
        initialValue="postgresql://synapse:***@synapse-db-ro.synapse.svc:5432/synapse"
        label="Connection (réplicas RO — lecture)"
        hydratedValue={getValue("network.db_connection_ro", "postgresql://synapse:***@synapse-db-ro.synapse.svc:5432/synapse")}
        hydratedPersisted={hasPersisted("network.db_connection_ro")}
      />
      <SettingsInput
        settingKey="network.db_pool_max"
        initialValue="10"
        label="Pool max (connexions par pod)"
        type="number"
        hydratedValue={getValue("network.db_pool_max", "10")}
        hydratedPersisted={hasPersisted("network.db_pool_max")}
      />
      <SettingsInput
        settingKey="network.redis_url"
        initialValue="redis://synapse-redis.synapse.svc:6379/0"
        label="Redis"
        showTestButton
        hydratedValue={getValue("network.redis_url", "redis://synapse-redis.synapse.svc:6379/0")}
        hydratedPersisted={hasPersisted("network.redis_url")}
      />

      <div style={{ fontSize: "10px", color: "var(--muted)", lineHeight: 1.5, marginTop: "8px" }}>
        Le statut ci-dessus provient de pings réels (<code>SELECT 1</code> / <code>PING</code>) via l'API.
        Les chaînes de connexion sont affichées à titre de <strong>topologie/documentation</strong> (mot de passe
        masqué <code>***</code>) — la connexion effective des pods reste pilotée par les <strong>secrets Kubernetes CNPG</strong>.
        Schéma géré par migrations SQL (<code>shared/migrations</code>).
      </div>
    </div>
  );
}

function NetworkSettings({ getValue, hasPersisted }: SettingsPanelProps) {
  return (
    <>
      <SettingsCard icon="🌐" title="Réseau">
        <SettingsInput
          settingKey="network.domain"
          initialValue="synapse.ia.technosecur.com"
          label="Domaine"
          hydratedValue={getValue("network.domain", "synapse.ia.technosecur.com")}
          hydratedPersisted={hasPersisted("network.domain")}
        />
        <SettingsInput
          settingKey="network.api_port"
          initialValue="8000"
          label="Port API"
          type="number"
          hydratedValue={getValue("network.api_port", "8000")}
          hydratedPersisted={hasPersisted("network.api_port")}
        />
        <SettingsInput
          settingKey="network.cors_origin"
          initialValue="https://synapse.ia.technosecur.com"
          label="CORS"
          hydratedValue={getValue("network.cors_origin", "https://synapse.ia.technosecur.com")}
          hydratedPersisted={hasPersisted("network.cors_origin")}
        />
      </SettingsCard>
      <DatabaseStatusCard getValue={getValue} hasPersisted={hasPersisted} />
      <SettingsCard icon="🛡" title="Sécurité">
        <Field label="CF WAF">
          <div className="select-row">
            <span className="select-pill active">Activé</span>
            <span className="select-pill">Désactivé</span>
          </div>
        </Field>
        <Field label="SSL/TLS">
          <div className="select-row">
            <span className="select-pill active">Full (strict)</span>
          </div>
        </Field>
      </SettingsCard>
    </>
  );
}

function AppearanceSettings() {
  return (
    <SettingsCard icon="🎨" title="Thème">
      <Field label="Palette">
        <div className="select-pill active" style={{ padding: "14px", display: "grid", gap: "4px" }}>
          <span style={{ fontWeight: 600 }}>Mauve cosmique</span>
          <div style={{ display: "flex", gap: "3px" }}>
            <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#b794f6" }} />
            <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#6b8dd6" }} />
            <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#5ec7a8" }} />
          </div>
        </div>
      </Field>
    </SettingsCard>
  );
}

function NotificationsSettings() {
  return (
    <>
      <SettingsCard icon="🔔" title="Canaux de notification">
        <Field label="Cloche interne (WebUI)">
          <div className="select-row">
            <span className="select-pill active">Activée</span>
            <span className="select-pill">Désactivée</span>
          </div>
        </Field>
        <Field label="Discord">
          <div className="select-row">
            <span className="select-pill active">Activé</span>
            <span className="select-pill">Désactivé</span>
          </div>
        </Field>
        <Field label="Web push (navigateur)">
          <div className="select-row">
            <span className="select-pill active">Activé</span>
            <span className="select-pill">Désactivé</span>
          </div>
        </Field>
      </SettingsCard>
      <SettingsCard icon="⏸" title="Heures silencieuses">
        <Field label="Mode silencieux">
          <div className="select-row">
            <span className="select-pill">Jamais</span>
            <span className="select-pill active">22:00 → 07:00</span>
            <span className="select-pill">Personnalisé</span>
          </div>
        </Field>
        <Field label="Exceptions critiques">
          <div className="select-row">
            <span className="select-pill active">Toujours notifier</span>
            <span className="select-pill">Même en silencieux</span>
          </div>
        </Field>
      </SettingsCard>
      <SettingsCard icon="📋" title="Routage par événement">
        <Field label="Critiques (DB, auth, pods)">
          <div className="select-row">
            <span className="select-pill active">Cloche + Discord + Web</span>
            <span className="select-pill">Cloche + Discord</span>
          </div>
        </Field>
        <Field label="Missions">
          <div className="select-row">
            <span className="select-pill">Cloche + Discord + Web</span>
            <span className="select-pill active">Cloche + Discord</span>
          </div>
        </Field>
        <Field label="CI/CD">
          <div className="select-row">
            <span className="select-pill">Cloche + Discord + Web</span>
            <span className="select-pill active">Cloche + Web</span>
          </div>
        </Field>
        <Field label="Approvals">
          <div className="select-row">
            <span className="select-pill active">Cloche + Discord + Web</span>
            <span className="select-pill">Cloche + Web</span>
          </div>
        </Field>
      </SettingsCard>
    </>
  );
}

function IntegrationsHealthSettings({ getValue, hasPersisted }: SettingsPanelProps) {
  // CFG-06: état réel des briques via GET /api/health/integrations.
  const [services, setServices] = useState<
    { key: string; name: string; status: string; latency: string; detail: string; latencyMs: number | null }[]
  >([]);
  const [loaded, setLoaded] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await api.health.integrations();
      // Map statut API → statut d'affichage (healthy/degraded/error/unconfigured)
      const uiStatus: Record<string, string> = {
        ok: "healthy",
        degraded: "degraded",
        down: "error",
        unconfigured: "unconfigured",
      };
      setServices(
        data.map((d) => ({
          key: d.key,
          name: d.label,
          status: uiStatus[d.status] ?? "unconfigured",
          latency: d.latencyMs != null ? `${d.latencyMs}ms` : (d.detail ?? "—"),
          detail: d.detail ?? "",
          latencyMs: d.latencyMs ?? null,
        })),
      );
      setLastRefresh(new Date());
    } catch {
      setServices([]);
    } finally {
      setLoaded(true);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    load();
    const interval = setInterval(() => { if (alive) load(); }, 30_000);
    return () => { alive = false; clearInterval(interval); };
  }, [load]);

  const statusColor: Record<string, string> = { healthy: "var(--olive)", degraded: "var(--amber)", error: "var(--rust)", unconfigured: "var(--muted)" };
  const statusLabel: Record<string, string> = { healthy: "opérationnel", degraded: "dégradé", error: "en erreur", unconfigured: "non configuré" };

  // Icône par service (via la clé stable renvoyée par l'API)
  const serviceIcon = (key: string): string => {
    if (key === "db") return "🗄";
    if (key === "redis") return "⚡";
    if (key.startsWith("llm")) return "🧠";
    if (key.startsWith("github")) return "🐱";
    if (key === "discord") return "💬";
    if (key === "obsidian") return "📓";
    return "📡";
  };

  // Barre de latence normalisée (0–500ms → 0–100%)
  const latencyBar = (ms: number | null): number => {
    if (ms == null) return 0;
    return Math.min(100, Math.round((ms / 500) * 100));
  };

  const counts = {
    total: services.length,
    healthy: services.filter((s) => s.status === "healthy").length,
    degraded: services.filter((s) => s.status === "degraded").length,
    error: services.filter((s) => s.status === "error").length,
  };
  const globalOk = counts.total > 0 && counts.error === 0 && counts.degraded === 0;

  return (
    <>
      <div className="stats-row">
        <div className="stat-card"><div className="eyebrow">suivies</div><div className="val">{counts.total}</div></div>
        <div className="stat-card"><div className="eyebrow">saines</div><div className="val" style={{ color: "var(--olive)" }}>{counts.healthy}</div></div>
        <div className="stat-card"><div className="eyebrow">dégradées</div><div className="val" style={{ color: "var(--amber)" }}>{counts.degraded}</div></div>
        <div className="stat-card"><div className="eyebrow">en erreur</div><div className="val" style={{ color: "var(--rust)" }}>{counts.error}</div></div>
      </div>

      <div className="settings-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
            📡 Grille de santé
            <span style={{
              fontSize: "10px", fontWeight: 600, padding: "3px 9px", borderRadius: "999px",
              background: globalOk ? "rgba(94,199,168,0.15)" : "rgba(214,158,74,0.15)",
              color: globalOk ? "var(--olive)" : "var(--amber)",
            }}>{globalOk ? "● tous systèmes nominaux" : "● attention requise"}</span>
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {lastRefresh && (
              <span style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
                maj {lastRefresh.toLocaleTimeString("fr-FR")}
              </span>
            )}
            <button className="btn" style={{ fontSize: "11px", padding: "6px 10px" }} onClick={load} disabled={refreshing} title="Rafraîchir">
              {refreshing ? <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</span> : "↻"}
            </button>
          </div>
        </div>

        {loaded && services.length === 0 && (
          <div style={{ padding: "24px", textAlign: "center", color: "var(--muted)", fontSize: "13px" }}>
            Données de santé indisponibles (API injoignable)
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "12px" }}>
          {services.map((s) => {
            const color = statusColor[s.status];
            return (
              <div key={s.key} style={{
                position: "relative", padding: "14px 14px 14px 16px", borderRadius: "16px",
                background: "rgba(0,0,0,0.16)", border: `1px solid ${color}28`,
                borderLeft: `3px solid ${color}`, display: "flex", flexDirection: "column",
                gap: "10px", overflow: "hidden", minHeight: "104px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{
                    width: "34px", height: "34px", borderRadius: "10px", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "17px", background: `${color}18`,
                  }}>{serviceIcon(s.key)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: "13px", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</strong>
                    <span style={{ fontSize: "10px", color, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {statusLabel[s.status]}
                    </span>
                  </div>
                  <span style={{
                    width: "8px", height: "8px", borderRadius: "999px", background: color, flexShrink: 0,
                    boxShadow: s.status === "healthy" ? `0 0 6px ${color}` : undefined,
                    animation: s.status === "healthy" ? "pulse 2s ease infinite" : undefined,
                  }} />
                </div>

                <div style={{ marginTop: "auto", display: "grid", gap: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "var(--mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {s.detail || "—"}
                    </span>
                    <span style={{ fontSize: "10px", color, fontFamily: "var(--mono)", fontWeight: 600, flexShrink: 0 }}>
                      {s.latencyMs != null ? s.latency : "n/a"}
                    </span>
                  </div>

                  {/* Barre de latence toujours présente (track vide si non mesurable) → cartes alignées */}
                  <div style={{ height: "3px", borderRadius: "999px", background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                    {s.latencyMs != null && (
                      <div style={{ height: "100%", width: `${latencyBar(s.latencyMs)}%`, background: color, borderRadius: "999px", transition: "width 0.4s" }} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <SettingsCard icon="⏱" title="Surveillance">
        <SettingsInput
          settingKey="health.check_interval"
          initialValue="30"
          label="Intervalle de check (s)"
          type="number"
          hydratedValue={getValue("health.check_interval", "30")}
          hydratedPersisted={hasPersisted("health.check_interval")}
        />
        <SettingsInput
          settingKey="health.timeout_ms"
          initialValue="5000"
          label="Timeout par check (ms)"
          type="number"
          hydratedValue={getValue("health.timeout_ms", "5000")}
          hydratedPersisted={hasPersisted("health.timeout_ms")}
        />
        <SettingsInput
          settingKey="health.fail_threshold"
          initialValue="3"
          label="Alerte après N échecs"
          type="number"
          hydratedValue={getValue("health.fail_threshold", "3")}
          hydratedPersisted={hasPersisted("health.fail_threshold")}
        />
      </SettingsCard>
    </>
  );
}

function DrpSettings({ getValue, hasPersisted }: SettingsPanelProps) {
  const navigate = useNavigate();
  const [steps, setSteps] = useState<DrpStepInfo[] | null>(null);
  const [testingS3, setTestingS3] = useState(false);
  const [s3Result, setS3Result] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    let alive = true;
    api.drp.steps()
      .then((s) => { if (alive) setSteps(s as DrpStepInfo[]); })
      .catch(() => { if (alive) setSteps(null); });
    return () => { alive = false; };
  }, []);

  const runbook = {
    total: steps?.length ?? 0,
    enabled: steps?.filter((s) => s.enabled).length ?? 0,
    done: steps?.filter((s) => s.state === "done").length ?? 0,
  };
  const runbookReady = runbook.total > 0 && runbook.enabled === runbook.total;

  const bucketConfigured = hasPersisted("drp.wasabi_bucket") && hasPersisted("drp.wasabi_endpoint");
  const s3KeysConfigured = hasPersisted("drp.wasabi_access_key") && hasPersisted("drp.wasabi_secret_key");
  const s3Ready = bucketConfigured && s3KeysConfigured;

  const testS3 = async () => {
    setTestingS3(true);
    setS3Result(null);
    try {
      // Teste la clé d'accès Wasabi (validation format + réseau via /settings test)
      const res = await api.settings.test("drp.wasabi_access_key");
      setS3Result({ ok: res.ok, message: res.message });
      if (res.ok) showToast("success", "Wasabi S3", res.message);
      else showToast("error", "Wasabi S3", res.message);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur réseau";
      setS3Result({ ok: false, message: msg });
      showToast("error", "Wasabi S3", msg);
    } finally {
      setTestingS3(false);
    }
  };

  return (
    <>
      <div className="stats-row">
        <div className="stat-card"><div className="eyebrow">RTO cible</div><div className="val" style={{ color: "var(--amber)" }}>2h</div></div>
        <div className="stat-card"><div className="eyebrow">RPO cible</div><div className="val" style={{ color: "var(--olive)" }}>15min</div></div>
        <div className="stat-card">
          <div className="eyebrow">backups S3</div>
          <div className="val" style={{ color: s3Ready ? "var(--olive)" : "var(--amber)" }}>{s3Ready ? "✓" : "⚠"}</div>
        </div>
        <div className="stat-card">
          <div className="eyebrow">runbook DRP</div>
          <div className="val" style={{ color: runbookReady ? "var(--olive)" : "var(--muted)" }}>
            {steps == null ? "—" : `${runbook.enabled}/${runbook.total}`}
          </div>
        </div>
      </div>

      {/* Bandeau statut de reprise + accès au runbook */}
      <div className="settings-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "grid", gap: "4px" }}>
            <h3 style={{ margin: 0 }}>🆘 État de la reprise</h3>
            <span style={{ fontSize: "12px", color: "var(--muted)" }}>
              {steps == null
                ? "Runbook indisponible (API injoignable)"
                : `${runbook.total} étapes · ${runbook.enabled} activées · ${runbook.done} déjà exécutées`}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{
              fontSize: "11px", fontWeight: 600, padding: "5px 12px", borderRadius: "999px",
              background: runbookReady ? "rgba(94,199,168,0.15)" : "rgba(214,158,74,0.15)",
              color: runbookReady ? "var(--olive)" : "var(--amber)",
            }}>{runbookReady ? "● prêt à exécuter" : "● à vérifier"}</span>
            <button className="btn primary" style={{ fontSize: "12px", padding: "8px 14px" }} onClick={() => navigate("/drp")}>
              Ouvrir le runbook →
            </button>
          </div>
        </div>
      </div>

      <SettingsCard icon="☁️" title="Wasabi S3 (backups off-site)">
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span style={{
            width: "9px", height: "9px", borderRadius: "999px",
            background: s3Ready ? "var(--olive)" : "var(--amber)",
          }} />
          <span style={{ fontSize: "12px", color: s3Ready ? "var(--olive)" : "var(--amber)", fontWeight: 600 }}>
            {s3Ready ? "Configuré (bucket + endpoint + clés)" : bucketConfigured ? "Bucket OK — clés manquantes" : "Non configuré"}
          </span>
        </div>
        <SettingsInput
          settingKey="drp.wasabi_bucket"
          initialValue="synapse-backups"
          label="Bucket"
          hydratedValue={getValue("drp.wasabi_bucket", "synapse-backups")}
          hydratedPersisted={hasPersisted("drp.wasabi_bucket")}
        />
        <SettingsInput
          settingKey="drp.wasabi_endpoint"
          initialValue="https://s3.wasabisys.com"
          label="Endpoint"
          hydratedValue={getValue("drp.wasabi_endpoint", "https://s3.wasabisys.com")}
          hydratedPersisted={hasPersisted("drp.wasabi_endpoint")}
        />
        <SettingsInput
          settingKey="drp.wasabi_region"
          initialValue="us-east-1"
          label="Région"
          hydratedValue={getValue("drp.wasabi_region", "us-east-1")}
          hydratedPersisted={hasPersisted("drp.wasabi_region")}
        />
        <SettingsInput
          settingKey="drp.wasabi_access_key"
          initialValue=""
          label="Access Key"
          type="password"
          isSecret
          hydratedValue={getValue("drp.wasabi_access_key", "")}
          hydratedPersisted={hasPersisted("drp.wasabi_access_key")}
        />
        <SettingsInput
          settingKey="drp.wasabi_secret_key"
          initialValue=""
          label="Secret Key"
          type="password"
          isSecret
          hydratedValue={getValue("drp.wasabi_secret_key", "")}
          hydratedPersisted={hasPersisted("drp.wasabi_secret_key")}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "6px" }}>
          <button className="btn" style={{ fontSize: "12px", padding: "8px 14px" }} onClick={testS3} disabled={testingS3 || !s3KeysConfigured}>
            {testingS3 ? "⟳ Test…" : "🔍 Tester Wasabi"}
          </button>
          {s3Result && (
            <span style={{ fontSize: "11px", color: s3Result.ok ? "var(--olive)" : "var(--rust)", fontFamily: "var(--mono)" }}>
              {s3Result.ok ? "✓" : "✗"} {s3Result.message}
            </span>
          )}
        </div>
      </SettingsCard>

      <SettingsCard icon="💾" title="Longhorn Snapshots (backups locaux)">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px", marginBottom: "12px" }}>
          <div style={{ padding: "10px", borderRadius: "12px", background: "rgba(0,0,0,0.12)", textAlign: "center" }}>
            <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>Volumes protégés</div>
            <div style={{ fontSize: "13px", fontWeight: 600, fontFamily: "var(--mono)" }}>PVC synapse</div>
          </div>
          <div style={{ padding: "10px", borderRadius: "12px", background: "rgba(0,0,0,0.12)", textAlign: "center" }}>
            <div style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>Réplication</div>
            <div style={{ fontSize: "13px", fontWeight: 600, fontFamily: "var(--mono)", color: "var(--olive)" }}>3 replicas</div>
          </div>
        </div>
        <Field label="Fréquence snapshots">
          <div className="select-row">
            <span className="select-pill active">Quotidien 02:00</span>
            <span className="select-pill">6h</span>
          </div>
        </Field>
        <SettingsInput
          settingKey="drp.retention"
          initialValue="7"
          label="Rétention (jours)"
          type="number"
          hydratedValue={getValue("drp.retention", "7")}
          hydratedPersisted={hasPersisted("drp.retention")}
        />
        <Field label="Backup S3 (Longhorn → Wasabi)">
          <div className="select-row">
            <span className={`select-pill ${s3Ready ? "active" : ""}`}>Activé</span>
            <span className={`select-pill ${!s3Ready ? "active" : ""}`}>Désactivé</span>
          </div>
        </Field>
        {!s3Ready && (
          <div style={{ fontSize: "10px", color: "var(--amber)", marginTop: "4px" }}>
            ⚠ Le backup S3 des snapshots Longhorn nécessite les clés Wasabi ci-dessus.
          </div>
        )}
      </SettingsCard>
    </>
  );
}

// ── Main component ──────────────────────────────────────────────

export default function Settings() {
  const [activeTab, setActiveTab] = useState<TabKey>("llm");
  const [settings, setSettings] = useState<SettingRow[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Load initial settings from API
  useEffect(() => {
    apiListSettings().then((rows) => {
      setSettings(rows);
      setLoaded(true);
    });
  }, []);

  /** Helper: read value for a key (falls back to default) */
  const getValue = (key: string, fallback: string): string => {
    const row = settings.find((s) => s.key === key);
    if (!row) return fallback;
    // Si secret masqué, on garde le fallback
    if (row.isSecret && row.value === "••••••••") return fallback;
    return row.value;
  };

  /** Helper: a-t-on une valeur persistée (pour afficher ✓) */
  const hasPersisted = (key: string): boolean => {
    const row = settings.find((s) => s.key === key);
    return !!row && !!row.value;
  };

  /** Helper: aperçu masqué d'un secret (3 premiers + 5 derniers), si dispo */
  const getPreview = (key: string): string | undefined => {
    const row = settings.find((s) => s.key === key);
    return row?.preview;
  };

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">configuration du vaisseau</div>
          <h1>Settings</h1>
          <p>Configuration des intégrations, clés API, modes de contrôle et préférences.</p>
        </div>
      </div>

      <div className="settings-layout">
        {/* Sidebar tabs */}
        <nav className="settings-nav">
          <div className="settings-nav-inner">
            <div className="settings-nav-label">Intégrations</div>
            {TABS.slice(0, 4).map((tab) => (
              <SettingsTab key={tab.key} tab={tab} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} />
            ))}
            <div className="settings-nav-label">Système</div>
            {TABS.slice(4).map((tab) => (
              <SettingsTab key={tab.key} tab={tab} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} />
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="settings-content">
          <div className="settings-content-header">
            <h2>{TABS.find((t) => t.key === activeTab)?.icon} {TABS.find((t) => t.key === activeTab)?.label}</h2>
            <p>{TABS.find((t) => t.key === activeTab)?.desc}</p>
          </div>

          <div className="settings-panel active">
            <div className="settings-grid">
              {!loaded && <div className="no-results">Chargement des paramètres…</div>}
              {loaded && activeTab === "llm" && (
                <LlmSettings getValue={getValue} hasPersisted={hasPersisted} getPreview={getPreview} />
              )}
              {loaded && activeTab === "github" && (
                <GithubSettings getValue={getValue} hasPersisted={hasPersisted} getPreview={getPreview} />
              )}
              {loaded && activeTab === "discord" && (
                <DiscordSettings getValue={getValue} hasPersisted={hasPersisted} getPreview={getPreview} />
              )}
              {loaded && activeTab === "obsidian" && (
                <ObsidianSettings getValue={getValue} hasPersisted={hasPersisted} getPreview={getPreview} />
              )}
              {loaded && activeTab === "notifications" && <NotificationsSettings />}
              {loaded && activeTab === "control" && (
                <ControlSettings getValue={getValue} hasPersisted={hasPersisted} />
              )}
              {loaded && activeTab === "commanders" && (
                <CommandersSettings getValue={getValue} hasPersisted={hasPersisted} />
              )}
              {loaded && activeTab === "integrations" && (
                <IntegrationsHealthSettings getValue={getValue} hasPersisted={hasPersisted} />
              )}
              {loaded && activeTab === "network" && (
                <NetworkSettings getValue={getValue} hasPersisted={hasPersisted} />
              )}
              {loaded && activeTab === "appearance" && <AppearanceSettings />}
              {loaded && activeTab === "drp" && (
                <DrpSettings getValue={getValue} hasPersisted={hasPersisted} />
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function SettingsTab({ tab, active, onClick }: { tab: typeof TABS[number]; active: boolean; onClick: () => void }) {
  return (
    <div className={`settings-nav-item ${active ? "active" : ""}`} onClick={onClick}>
      <span className="s-icon">{tab.icon}</span>
      <span>
        <span>{tab.label}</span>
        <span className="s-desc">{tab.desc}</span>
      </span>
    </div>
  );
}